import { useCallback } from 'react';

import { useIsFeatureFlagLoaded } from '@shared/v2/contexts/featureFlags';
import useQueuedCallback from '@shared/v2/hooks/useQueuedCallback/useQueuedCallback';
import { SearchQueryValues } from '@shared/v2/hooks/useSearchQuery';

import useDestination from '@/hooks/useDestination';
import { useDestination as v2UseDestination } from '@/v2/contexts/destination';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import { dataLayer } from '@/v2/utils/dataLayer';

type CheapestOfferData = {
  packageOffers?: any[];
};

type Params = SearchQueryValues;

const useViewSearchResultsDataLayerEvent = () => {
  const shouldUseNewEvents = useGA4Events();
  const areFeatureFlagsReady = useIsFeatureFlagLoaded();
  const d = useDestination();
  const dV2 = v2UseDestination();
  const destination = d || dV2;

  const handleViewSearchResultsDataLayerEvent = useCallback(
    (data: CheapestOfferData, params: Params) => {
      const {
        originCode,
        departureDate,
        returnDate,
        adults,
        infants,
        children,
      } = params || {};

      // End early if missing any of the core search params
      if (
        !originCode ||
        !departureDate ||
        !returnDate ||
        adults == undefined ||
        infants == undefined ||
        children == undefined
      ) {
        return;
      }

      const destinationTitle = destination?.theme
        ? undefined
        : destination?.title;

      const origin = destination?.searchConfig?.origins?.find(
        (origin: any) => origin?.code === originCode,
      );

      if (!origin) {
        return;
      }

      dataLayer.viewSearchResultsEvent({
        numResults: data?.packageOffers?.length ?? 0,
        startDate: departureDate,
        endDate: returnDate,
        origin: origin?.name,
        destination: destinationTitle,
        adults: adults,
        children: children,
        infants: infants,
      });
    },
    [
      destination?.searchConfig?.origins,
      destination?.theme,
      destination?.title,
    ],
  );

  const [queuedHandleViewSearchResultsDataLayerEvent] = useQueuedCallback(
    handleViewSearchResultsDataLayerEvent,
    {
      // always queue all events unless flag is ready and should use new events.
      isCallbackReady: areFeatureFlagsReady && shouldUseNewEvents,
      autoRunQueueWhenReady: true,
    },
  );

  return queuedHandleViewSearchResultsDataLayerEvent;
};

export default useViewSearchResultsDataLayerEvent;
